// pages/DwiSendCities.jsx
// Admin → DwiSend → Cities (DwiSend Regional M3, first piece): the cities
// DwiSend serves, per country — add, edit, hide/restore, and delete a city
// nothing uses yet. Admin only; customers see nothing from this. Behind the
// dwisend_routes_admin switch (starts OFF), which can be turned on here.
// A city alone takes no orders: routes between cities must be approved first.
import { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import { countryAPI, dwisendCityAPI } from '../services/api';
import { Button, Card, Input, Select, Badge, Modal, Table, Td, Empty, Spinner, SectionHeader } from '../components/ui';

export function CitiesSection() {
  const [enabled, setEnabled]     = useState(null);   // null = loading
  const [countries, setCountries] = useState([]);
  const [country, setCountry]     = useState('');
  const [cities, setCities]       = useState([]);
  const [loading, setLoading]     = useState(false);
  const [editing, setEditing]     = useState(null);

  useEffect(() => {
    dwisendCityAPI.status().then(r => setEnabled(!!r.enabled)).catch(err => toast.error(err.message));
    countryAPI.adminList().then(r => {
      const list = r.countries || [];
      setCountries(list);
      const pick = list.find(c => c.code === 'CD') || list.find(c => !c.isDefault) || list[0];
      if (pick) setCountry(pick.code);
    }).catch(err => toast.error(err.message));
  }, []);

  const load = useCallback(async () => {
    if (!enabled || !country) return;
    setLoading(true);
    try { setCities((await dwisendCityAPI.list(country)).cities || []); }
    catch (err) { toast.error(err.message); }
    finally { setLoading(false); }
  }, [enabled, country]);
  useEffect(() => { load(); }, [load]);

  const flip = async on => {
    if (on && !window.confirm('Turn on DwiSend cities & routes? This only opens the admin screens — customers see nothing from it.')) return;
    try { setEnabled((await dwisendCityAPI.setSwitch(on)).enabled); toast.success(on ? 'Cities & routes switched ON' : 'Switched OFF'); }
    catch (err) { toast.error(err.message); }
  };

  const toggle = async c => {
    try { await dwisendCityAPI.update(c._id, { isActive: !c.isActive }); toast.success(c.isActive ? `${c.name} hidden` : `${c.name} restored`); load(); }
    catch (err) { toast.error(err.message); }
  };

  const remove = async c => {
    if (!window.confirm(`Delete ${c.name}? Only do this for a mistake — otherwise hide it.`)) return;
    try { await dwisendCityAPI.remove(c._id); toast.success(`${c.name} deleted`); load(); }
    catch (err) { toast.error(err.message); }
  };

  if (enabled === null) return <div className="py-10 flex justify-center"><Spinner /></div>;

  if (!enabled) {
    return (
      <Card>
        <SectionHeader title="🏙️ Cities" />
        <p className="text-sm text-text-secondary mb-3">
          DwiSend cities &amp; routes are <strong>switched OFF</strong>. Turning this on opens the admin screens for cities
          (and, later, routes, partners and prices). Customers see nothing from it.
        </p>
        <Button size="sm" onClick={() => flip(true)}>Turn on</Button>
      </Card>
    );
  }

  const current = countries.find(c => c.code === country);
  return (
    <Card>
      <SectionHeader title="🏙️ Cities"
        action={<div className="flex gap-2">
          <Button size="sm" variant="ghost" onClick={() => flip(false)}>Turn off</Button>
          <Button size="sm" disabled={!country} onClick={() => setEditing({})}>+ Add city</Button>
        </div>} />
      <p className="text-xs text-text-muted mb-3">
        The cities DwiSend serves in each country. A city alone takes no orders — a route between two cities must be
        checked and approved first. Hide a city to stop using it; delete is only for a mistake nothing uses yet.
      </p>
      <div className="flex gap-3 items-end mb-4 flex-wrap">
        <Select label="Country" value={country} onChange={e => setCountry(e.target.value)} className="min-w-[220px]">
          {countries.map(c => <option key={c.code} value={c.code}>{c.flag || ''} {c.name} ({c.code}){c.isActive ? '' : ' — inactive'}</option>)}
        </Select>
        {current && !current.isActive && <p className="text-xs text-text-muted pb-3">Inactive country: sign-ups stay closed — you can still prepare its cities.</p>}
      </div>
      {countries.length === 0 ? <Empty title="No countries yet" description="Add the country under Admin → 🌍 Countries first (leave it inactive)." />
        : loading ? <div className="py-10 flex justify-center"><Spinner /></div>
        : cities.length === 0 ? <Empty title={`No cities for ${current?.name || country} yet`} description="Use “+ Add city”, e.g. Kisangani." />
        : (
          <Table headers={['Order', 'City', 'Province', 'Notes', 'Used by', 'Status', '']}>
            {cities.map(c => (
              <tr key={c._id} className={c.isActive ? '' : 'opacity-50'}>
                <Td>{c.sortOrder}</Td>
                <Td className="text-text-primary font-semibold">{c.name}</Td>
                <Td>{c.province || '—'}</Td>
                <Td><div className="text-xs max-w-[220px] truncate">{c.notes || '—'}</div></Td>
                <Td>{c.usedBy ? `${c.usedBy} route${c.usedBy === 1 ? '' : 's'}` : '—'}</Td>
                <Td><Badge label={c.isActive ? 'Active' : 'Hidden'} color={c.isActive ? 'green' : 'gray'} /></Td>
                <Td>
                  <div className="flex gap-1">
                    <Button size="xs" variant="secondary" onClick={() => setEditing(c)}>Edit</Button>
                    <Button size="xs" variant="ghost" onClick={() => toggle(c)}>{c.isActive ? 'Hide' : 'Restore'}</Button>
                    {!c.usedBy && <Button size="xs" variant="ghost" onClick={() => remove(c)}>Delete</Button>}
                  </div>
                </Td>
              </tr>
            ))}
          </Table>
        )}
      {editing && <CityModal city={editing} countryCode={country} countryName={current?.name || country}
        onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </Card>
  );
}

function CityModal({ city, countryCode, countryName, onClose, onSaved }) {
  const isNew = !city._id;
  const [form, setForm] = useState({
    name: city.name || '', province: city.province || '', sortOrder: city.sortOrder ?? 0, notes: city.notes || '',
  });
  const [saving, setSaving] = useState(false);
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    const body = { name: form.name.trim(), province: form.province.trim(), notes: form.notes.trim(),
      sortOrder: String(form.sortOrder).trim() === '' ? 0 : Number(form.sortOrder) };
    setSaving(true);
    try {
      if (isNew) await dwisendCityAPI.create({ ...body, countryCode }); else await dwisendCityAPI.update(city._id, body);
      toast.success(isNew ? `${body.name} added to ${countryName}` : 'Saved');
      onSaved();
    } catch (err) { toast.error(err.message); }
    finally { setSaving(false); }
  };

  return (
    <Modal open onClose={onClose} title={`${isNew ? 'Add city' : 'Edit city'} — ${countryName}`} size="sm">
      <div className="space-y-3">
        <Input label="City" placeholder="e.g. Kisangani" value={form.name} onChange={set('name')} />
        <Input label="Province (optional)" placeholder="e.g. Tshopo" value={form.province} onChange={set('province')} />
        <Input label="Order (lower shows first)" type="number" step="1" value={form.sortOrder} onChange={set('sortOrder')} />
        <Input label="Notes (admin only)" placeholder="e.g. reached via Bunia" value={form.notes} onChange={set('notes')} />
        <div className="flex gap-2">
          <Button onClick={save} loading={saving} disabled={!form.name.trim()}>{isNew ? 'Add city' : 'Save'}</Button>
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
        </div>
      </div>
    </Modal>
  );
}
